import React, { useContext, useEffect } from "react";

import { Preview } from "@creatomate/preview";
import { Icon, Panel, Tooltip, classnames } from "@culturehq/components";

import { ElementSource } from "chq";
import { useProfile } from "app/context";
import { t, Trans } from "app/utils";

import FolderIcon from "components/common/icons/FolderIcon";
import FolderOutlinedIcon from "components/common/icons/FolderOutlinedIcon";
import TextFilledIcon from "components/common/icons/TextFilledIcon";
import TextIcon from "components/common/icons/TextIcon";

import AccessibleFocusOutlineElement from "components/common/AccessibleFocusOutlineElement";
import checkRequiredDetails from "components/editor/checkRequiredDetails";
import { CreatomateContext } from "../contexts/VideoStudioContext";
import { isSubtitleText } from "./tabs/subtitle/utils";
import { IMAGE_TYPE, VIDEO_TYPE } from "../utils/constants";
import { StoryModalContext } from "../contexts/StoryContext";
import SubtitlesButton from "./SubtitlesButton";
import TemplatesButton from "./TemplatesButton";

interface MenuProps {
  mediaBeingEdited?: boolean;
  preview?: Preview;
}

const Menu: React.FC<MenuProps> = ({
  mediaBeingEdited,
  preview
}) => {
  const { profile } = useProfile();
  const { selectedBody } = useContext(StoryModalContext);
  const { data, handleUpdateData, getActiveElement } = useContext(CreatomateContext);
  const {
    activeMenuItem,
    blockSubtitles,
    isLastMediaVersionProcessing,
    mediaType,
    montage,
    storyQuestionId,
    subtitles
  } = data;
  const activeElement = preview ? getActiveElement(preview) : undefined;
  const { ready } = preview || { ready: false };
  const source = preview?.getSource();
  const mediaElements = source?.elements?.filter((element: ElementSource) => (
    element.type === VIDEO_TYPE || element.type === IMAGE_TYPE
  ));
  const subtitlesNotDeleted = subtitles.filter(subtitle => !subtitle.deleted);
  const blockSubtitlesTab = (blockSubtitles || isLastMediaVersionProcessing)
    && subtitlesNotDeleted.length === 0;

  const handleSelectedSubtitles = () => {
    if (blockSubtitles && subtitlesNotDeleted.length === 0 && !ready) return;

    handleChangeMenuItem("subtitles", true);
  };

  useEffect(
    () => {
      switch (activeElement?.source?.type) {
        case "text":
          if (isSubtitleText(activeElement?.source?.name)) {
            handleChangeMenuItem("subtitles");
          } else {
            handleChangeMenuItem("text");
          }
          break;
        case VIDEO_TYPE:
        case IMAGE_TYPE:
          handleChangeMenuItem("media");
          break;
        case "audio":
          handleChangeMenuItem("audio");
          break;
        default:
          break;
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeElement]
  );

  const handleChangeMenuItem = (value: string, menuClick = false) => {
    if (!menuClick && montage && activeMenuItem === "stories") return;
    handleUpdateData({ activeMenuItem: value });
  };

  return (
    <div className="sidebar">
      <Panel>
        <div>
          <div className="view-sec">
            <ul className="e_label">
              {montage && (
                <li className={classnames({ selected: activeMenuItem === "stories" })} onClick={() => handleChangeMenuItem("stories", true)}>
                  <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                    <Icon className="ac_icon" icon={activeMenuItem === "stories" ? "book-filled" : "book"} />
                    <Trans>Stories</Trans>
                  </AccessibleFocusOutlineElement>
                </li>
              )}
              <li className={classnames({ selected: activeMenuItem === "templates" })} onClick={() => handleChangeMenuItem("templates", true)}>
                {mediaElements?.length === 0 ? (
                  <Tooltip tip={montage ? t("Please select a story from the list") : t("Please add a media to be edited")}>
                    <TemplatesButton mediaElements={mediaElements} />
                  </Tooltip>
                ) : <TemplatesButton mediaElements={mediaElements} />}
              </li>
              {profile.culturehqAdmin && (
                <li className={classnames({ selected: activeMenuItem === "library" })} onClick={() => handleChangeMenuItem("library", true)}>
                  <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                    {activeMenuItem === "library" ? <FolderIcon /> : <FolderOutlinedIcon />}
                    <Trans>Library</Trans>
                  </AccessibleFocusOutlineElement>
                </li>
              )}
              {(mediaType === VIDEO_TYPE || montage) && (
                <li className={classnames({ selected: activeMenuItem === "audio" })} onClick={() => handleChangeMenuItem("audio", true)}>
                  <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                    {activeMenuItem === "audio" ? <Icon icon="musical-note-filled" /> : <Icon icon="musical-note" />}
                    <Trans>Audio</Trans>
                  </AccessibleFocusOutlineElement>
                </li>
              )}
              {(mediaType === VIDEO_TYPE && !montage && !mediaBeingEdited) && (
                <li className={classnames({ selected: activeMenuItem === "subtitles" })} onClick={handleSelectedSubtitles}>
                  {blockSubtitlesTab ? (
                    <Tooltip tip={<p className="Tooltip-replace">{t(isLastMediaVersionProcessing ? "Please wait for the current video edition to finish to generate the subtitles" : "Please save the video before generating subtitles")}</p> as unknown as string}>
                      <SubtitlesButton blockSubtitlesTab preview={preview} />
                    </Tooltip>
                  ) : <SubtitlesButton preview={preview} />}
                </li>
              )}
              <li className={classnames({ selected: activeMenuItem === "text" })} onClick={() => handleChangeMenuItem("text", true)}>
                <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                  {activeMenuItem === "text" ? <TextFilledIcon /> : <TextIcon />}
                  <Trans>Text</Trans>
                </AccessibleFocusOutlineElement>
              </li>
              <li className={classnames({ selected: activeMenuItem === "media" })} onClick={() => handleChangeMenuItem("media", true)}>
                <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                  <Icon className="images" icon="images" />
                  <Trans>Media</Trans>
                </AccessibleFocusOutlineElement>
              </li>
              {(mediaType === VIDEO_TYPE && !montage) && (
                <li className={classnames({ selected: activeMenuItem === "thumbnail" })} onClick={() => handleChangeMenuItem("thumbnail", true)}>
                  <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                    {activeMenuItem === "thumbnail" ? <Icon className="dot-user" icon="user-filled" /> : <Icon className="dotless-user" icon="user" />}
                    <Trans>Thumbnail</Trans>
                  </AccessibleFocusOutlineElement>
                </li>
              )}
              {profile.culturehqAdmin && (
                <li className={classnames({ selected: activeMenuItem === "json" })} onClick={() => handleChangeMenuItem("json", true)}>
                  <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                    <Icon
                      className="ac_icon"
                      icon={activeMenuItem === "json" ? "web-development-filled" : "web-development"}
                    />
                    <Trans>Json</Trans>
                  </AccessibleFocusOutlineElement>
                </li>
              )}
              {montage && (
                <li className={classnames({ selected: activeMenuItem === "details" })} onClick={() => handleChangeMenuItem("details", true)}>
                  <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                    {activeMenuItem === "details" ? <Icon icon="info-v2-filled" /> : <Icon icon="info-v2" />}
                    <Trans>Details</Trans>
                    {!checkRequiredDetails({ storyQuestionId, selectedBody })
                      ? <Icon className="Required-Icon Info-Icon" icon="info" />
                      : <Icon className="Completed-Icon Info-Icon" icon="ios-checkmark" />}
                  </AccessibleFocusOutlineElement>
                </li>
              )}
            </ul>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default Menu;
